import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  CompaniesService,
  Company,
  Country,
  CustomValidators,
  UserLanguage,
  UsersService
} from '@smartcharge/shared/index';
import { SharedModule } from '@smartcharge/shared/shared.module';

import { Intermediators } from '../../../classes/Invoice';
import { DirectivesModule } from '../../../directives/directives.module';
import { CountriesFacade, UserService } from '../../../lib';
import { RolesService } from '../../../services/roles/roles.service';
import { FilteredSelectModule } from '../../../shared/components/filtered-select/filtered-select.module';
import { FormErrorComponent } from '../../../shared/components/form-error/form-error.component';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { UserType } from '../../../user-roles';
import { CreateCustomerComponent } from '../../admin/create-customer/create-customer.component';
import { UserSearchModule } from '../../admin/users/user-search/user-search.module';

@Component({
  selector: 'sc-add-company',
  standalone: true,
  imports: [
    CommonModule,
    DirectivesModule,
    DragDropModule,
    FilteredSelectModule,
    FormsModule,
    FormErrorComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ReactiveFormsModule,
    SharedModule,
    TranslateModule,
    UserSearchModule
  ],
  templateUrl: './add-company.component.html',
  styleUrl: './add-company.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddCompanyComponent {
  readonly countries = signal<Country[]>(null);
  readonly departmentRequired = signal(false);
  readonly form: FormGroup;
  readonly languages = signal<UserLanguage[]>(null);
  readonly publicInformations = signal<Company | true>(null);
  readonly selectedCountry = computed(() => {
    return this.countries()?.find((country) => country.PK_CountryID === this.form.controls.FK_CountryID.value);
  });

  readonly COMMA = COMMA;
  readonly ENTER = ENTER;
  readonly Intermediators = Intermediators;
  readonly UserType = UserType;

  showIBAN = false;

  private isAdminOrOperator = this.rolesService.hasHighlevelAccess([UserType.Admin, UserType.Operator]);

  constructor(
    private readonly countriesFacade: CountriesFacade,
    private readonly companiesService: CompaniesService,
    private readonly dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<AddCompanyComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly rolesService: RolesService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly usersService: UsersService
  ) {
    this.form = this.formBuilder.group({
      OwnedByCustomerID: [
        this.rolesService.hasHighlevelAccess([UserType.Admin, UserType.Support])
          ? null
          : this.userService.getCustomerID()
      ],
      Name: [null, Validators.required],
      Department: [null],
      Phone: [null],
      Email: [null, [CustomValidators.email, Validators.required]],
      FK_CountryID: [1, Validators.required],
      TimezoneID: [Intl.DateTimeFormat().resolvedOptions().timeZone, Validators.required],
      Address: [null],
      PostCode: [null],
      City: [null],
      StateOrProvince: [null],
      CompanyNumber: [null, Validators.required],
      VATNumber: [null],
      HasVATBeenApproved: [true],
      InvoiceEmail: [[]],
      SettlementEmails: [[]],
      InvoiceName: [null],
      InvoiceAddress: [null],
      InvoiceAddress2: [null],
      InvoicePostCode: [null],
      InvoiceCity: [null],
      InvoiceStateOrProvince: [null],
      FK_InvoiceCountryID: [null],
      InvoiceFrequency: ['Monthly'],
      InvoicingLanguage: ['en'],
      BIC_SWIFT: [null],
      IBAN: [null],
      BankAccountNumberType: [null],
      BankAccountNumber: [null],

      InvoiceReference: ['Electric vehicle charging', Validators.maxLength(25)],
      EInvoicingAddress: [null, Validators.maxLength(50)],
      EInvoicingProvider: [],
      InvoiceReferencePayouts: ['Electric vehicle charging', [Validators.maxLength(25), CustomValidators.validKid]],

      IsCompany: [true],
      ApprovedByCustomer: [false],

      UseContactInfo: [true]
    });

    this.form.controls.IsCompany.valueChanges.subscribe((isCompany) => {
      if (isCompany) {
        this.form.controls.CompanyNumber.setValidators([Validators.required]);
      } else {
        this.form.controls.CompanyNumber.clearValidators();
      }

      this.form.controls.CompanyNumber.updateValueAndValidity();
    });

    this.countriesFacade.allCountries$.subscribe((countries) => {
      this.countries.set(countries);
    });

    this.usersService.getSupportedLanguages().subscribe((languages) => {
      this.languages.set(languages);
    });
  }

  addCustomer() {
    const dialogRef: MatDialogRef<CreateCustomerComponent> = this.dialog.open(CreateCustomerComponent, {
      width: '270px'
    });

    dialogRef.componentInstance.redirectToApp = false;
    dialogRef.componentInstance.showRedirectOption = false;

    dialogRef.afterClosed().subscribe((newCustomer) => {
      if (newCustomer) {
        this.selectCustomer(newCustomer);
      }
    });
  }

  checkCompanyNumber() {
    if (this.form.controls.IsCompany.value) {
      this.companiesService
        .searchOrgInfo(this.form.controls.CompanyNumber.value, this.form.controls.FK_CountryID.value)
        .subscribe({
          next: (companies: Company[]) => {
            if (companies.length > 0) {
              this.form.controls.Department.setValidators([Validators.required]);
              this.form.controls.Department.updateValueAndValidity();
              this.departmentRequired.set(true);
            }

            this.configureCountry();
          },
          error: () => {
            this.configureCountry();
          }
        });
    } else {
      this.configureCountry();
    }
  }

  selectCustomer(customer?) {
    this.form.controls.OwnedByCustomerID.patchValue(customer?.PK_CustomerID);
  }

  setupCompany(orgNumber: string, countryID: number) {
    if (orgNumber.match(/^[\d\s-]+$/)) {
      this.form.controls.CompanyNumber.patchValue(orgNumber);
    }
    this.form.controls.FK_CountryID.patchValue(countryID);
  }

  onSubmit() {
    if (this.form.invalid) {
      return;
    }

    const company: Company = {
      ...this.form.value,
      FK_CountryID: Number(this.form.controls.FK_CountryID.value),
      CompanyNumber: this.form.controls.CompanyNumber.value,
      ExtraDaysToPayInvoices: 0,
      isActive: true
    };

    if (!company.VATNumber && company.CompanyNumber) {
      company.VATNumber = company.CompanyNumber;
    }

    if (Array.isArray(company.SettlementEmails)) {
      company.SettlementEmails = company.SettlementEmails.join(';');
    }

    if (Array.isArray(company.InvoiceEmail)) {
      company.InvoiceEmail = company.InvoiceEmail.join(';');
    }

    this.companiesService.addCompany(company).subscribe({
      next: (result) => {
        this.dialogRef.close(result);
      },
      error: (err) => {
        this.toastService.error(err);
      }
    });
  }

  sequenceAdd(event: MatChipInputEvent, control: AbstractControl): void {
    const value = (event.value || '').trim();

    if (!value) {
      return;
    }

    control.setErrors(null);

    if (CustomValidators.email(control)) {
      control.setErrors({ email: true });
      control.markAsTouched();
    } else if (control.value.includes(value)) {
      control.setErrors({ notUnique: true });
      control.markAsTouched();
    } else {
      control.value.push(value);
      event.chipInput.clear();
    }
  }

  sequenceDrop(event: CdkDragDrop<any>, control: AbstractControl): void {
    moveItemInArray(control.value, event.previousIndex, event.currentIndex);

    control.markAsDirty();
  }

  sequenceInputChange(event: KeyboardEvent, control: AbstractControl): void {
    if (event.code === 'Enter') {
      return;
    }

    control.setErrors(null);
    control.updateValueAndValidity();
  }

  sequenceRemove(control: AbstractControl, value: string): void {
    const index = control.value.indexOf(value);

    if (index >= 0) {
      control.value.splice(index, 1);
    }
  }

  private configureCountry() {
    this.form.controls.IsCompany.disable();
    this.form.controls.CompanyNumber.disable();
    this.form.controls.FK_CountryID.disable();

    if (!this.form.controls.IsCompany.value) {
      this.form.patchValue({ CompanyNumber: '' });
      this.form.controls.HasVATBeenApproved.setValue(false);
    }

    this.fetchPublicInformations();

    if (this.selectedCountry()?.CountryCode === 'NO') {
      this.form.controls.VATNumber.clearValidators();
      this.form.controls.BIC_SWIFT.clearValidators();
      this.form.controls.IBAN.clearValidators();
      this.form.controls.BankAccountNumber.setValidators(this.isAdminOrOperator ? [] : [Validators.required]);
      this.form.controls.InvoiceReferencePayouts.setValidators([Validators.maxLength(25), CustomValidators.validKid]);
    } else if (this.selectedCountry()?.CountryCode === 'SWE') {
      this.form.controls.BIC_SWIFT.setValidators(this.isAdminOrOperator ? [] : [Validators.required]);
      this.form.controls.IBAN.setValidators(this.isAdminOrOperator ? [] : [Validators.required]);
      this.form.controls.BankAccountNumber.setValidators(this.isAdminOrOperator ? [] : [Validators.required]);

      if (!this.isAdminOrOperator) {
        this.showIBAN = true;
      }
    } else {
      if (this.form.controls.IsCompany.value) {
        this.form.controls.VATNumber.setValidators([Validators.required]);
      }

      this.form.controls.BIC_SWIFT.setValidators(this.isAdminOrOperator ? [] : [Validators.required]);
      this.form.controls.IBAN.setValidators(this.isAdminOrOperator ? [] : [Validators.required]);
      this.form.controls.IBAN.addValidators([Validators.pattern(this.selectedCountry().RegexIBAN)]);
      this.form.controls.InvoiceReferencePayouts.setValidators([Validators.maxLength(25)]);
      this.form.controls.BankAccountNumber.clearValidators();
    }

    this.form.updateValueAndValidity();
  }

  private fetchPublicInformations() {
    if (this.form.controls.IsCompany.value) {
      this.companiesService
        .getPublicInformation(this.form.controls.FK_CountryID.value, this.form.controls.CompanyNumber.value)
        .subscribe({
          next: (publicInformations) => {
            this.publicInformations.set(publicInformations);

            const { Name, Address, PostCode, City, StateOrProvince, VATNumber, Phone, Email } = publicInformations;
            this.form.patchValue({ Name, Address, PostCode, City, StateOrProvince, VATNumber, Phone, Email });
          },
          error: (err) => {
            if (err.status === 204) {
              this.publicInformations.set(true);
            } else {
              this.form.controls.IsCompany.enable();
              this.form.controls.CompanyNumber.enable();
              this.form.controls.FK_CountryID.enable();

              this.toastService.error(err);
            }
          }
        });
    } else {
      this.publicInformations.set(true);
    }
  }
}
